import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Box,
  Chip,
  SelectChangeEvent
} from '@mui/material';
import { GatewayWhatsapp } from '../../types';
import { gatewayWhatsappService } from '../../services/gatewayWhatsappService';
import GatewayWhatsappTypeIcon from '../icons/GatewayWhatsappTypeIcon';

interface GatewayWhatsappSelectProps {
  value: number | '';
  onChange: (value: number | '') => void;
  error?: boolean;
  helperText?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  showDefaultOption?: boolean;
  defaultOptionLabel?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

export const GatewayWhatsappSelect: React.FC<GatewayWhatsappSelectProps> = ({
  value,
  onChange,
  error = false,
  helperText,
  label = 'Gateway WhatsApp',
  required = false,
  disabled = false,
  showDefaultOption = true,
  defaultOptionLabel = 'Gateway Padrão',
  size = 'medium',
  fullWidth = true
}) => {
  const [gateways, setGateways] = useState<GatewayWhatsapp[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    loadGateways();
  }, []);

  const loadGateways = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      console.log('🔄 Carregando gateways WhatsApp no componente...');
      const data = await gatewayWhatsappService.getGatewaysWhatsappAtivos();
      console.log('✅ Gateways carregados:', data);
      console.log('📊 Quantidade de gateways:', data.length);
      setGateways(data);
    } catch (error) {
      console.error('❌ Erro ao carregar gateways WhatsApp:', error);
      setErrorMessage('Erro ao carregar gateways WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: SelectChangeEvent<number | ''>) => {
    const newValue = event.target.value;
    onChange(newValue === '' ? '' : Number(newValue));
  };

  const getGatewayDisplayName = (gateway: GatewayWhatsapp): string => {
    const tipoLabels: Record<string, string> = {
      'whaticket': 'Whaticket',
      'kangaroo': 'Kangaroo',
      'local': 'Local (Conexão Nativa)'
    };
    
    const tipoLabel = tipoLabels[gateway.tipo] || gateway.tipo;
    const statusIndicator = gateway.status_conectividade === 'conectado' ? '🟢' : 
                          gateway.status_conectividade === 'erro' ? '🔴' : '⚪';
    
    return `${statusIndicator} ${tipoLabel}`;
  };

  if (loading) {
    return (
      <FormControl fullWidth={fullWidth} error={error} required={required} size={size} variant="outlined">
        <InputLabel id="gateway-loading-label">{label}</InputLabel>
        <Select
          labelId="gateway-loading-label"
          value=""
          disabled
          label={label}
        >
          <MenuItem value="">
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <span>Carregando gateways...</span>
            </Box>
          </MenuItem>
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  if (errorMessage) {
    return (
      <FormControl fullWidth={fullWidth} error={true} required={required} size={size} variant="outlined">
        <InputLabel id="gateway-error-label">{label}</InputLabel>
        <Select
          labelId="gateway-error-label"
          value=""
          disabled
          label={label}
        >
          <MenuItem value="">
            <span style={{ color: 'red' }}>{errorMessage}</span>
          </MenuItem>
        </Select>
        <FormHelperText>Erro ao carregar gateways</FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth={fullWidth} error={error} required={required} size={size} variant="outlined">
      <InputLabel id="gateway-select-label">{label}</InputLabel>
      <Select
        labelId="gateway-select-label"
        value={value}
        onChange={handleChange}
        disabled={disabled}
        label={label}
        renderValue={(selected) => {
          if (selected == null || (typeof selected === 'string' && selected === '')) return null;
          if (selected === 9999) {
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label="Padrão" size="small" color="primary" variant="outlined" />
                <span>{defaultOptionLabel}</span>
              </Box>
            );
          }
          const gateway = gateways.find((g) => g.id === selected);
          if (!gateway) return String(selected);
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <GatewayWhatsappTypeIcon tipo={gateway.tipo} size={18} />
              <span>{gateway.nome}</span>
            </Box>
          );
        }}
      >
        {showDefaultOption && (
          <MenuItem value={9999}>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip 
                label="Padrão" 
                size="small" 
                color="primary" 
                variant="outlined"
              />
              <span>{defaultOptionLabel}</span>
            </Box>
          </MenuItem>
        )}
        
        {gateways.map((gateway) => (
          <MenuItem key={gateway.id} value={gateway.id}>
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <GatewayWhatsappTypeIcon tipo={gateway.tipo} size={20} />
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <span style={{ fontWeight: 'bold' }}>{gateway.nome}</span>
                  {gateway.is_default && (
                    <Chip 
                      label="PADRÃO" 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  )}
                </Box>
                <Box display="flex" alignItems="center" gap={1} fontSize="0.875rem" color="text.secondary">
                  <span>{getGatewayDisplayName(gateway).split(' ').slice(1).join(' ')}</span>
                  {gateway.status_conectividade === 'conectado' && (
                    <Chip 
                      label="Conectado" 
                      size="small" 
                      color="success" 
                      variant="outlined"
                    />
                  )}
                  {gateway.status_conectividade === 'erro' && (
                    <Chip 
                      label="Erro" 
                      size="small" 
                      color="error" 
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </MenuItem>
        ))}
        
        {gateways.length === 0 && (
          <MenuItem value="" disabled>
            <Box display="flex" alignItems="center" gap={1}>
              <span style={{ color: 'text.secondary' }}>
                Nenhum gateway WhatsApp ativo encontrado
              </span>
            </Box>
          </MenuItem>
        )}
      </Select>
      
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default GatewayWhatsappSelect;
